import React, { useEffect, useRef, useState } from "react";
import { Bot, ChevronDown, MessageCircle, Send, Sparkles, X } from "lucide-react";
import { supabase } from "../../supabaseClient";

// ── FAQ data (all 15 topics — quick replies still answer instantly/locally) ─

const FAQS = [
    {
        id: "services",
        label: "Services offered",
        question: "What counselling services do you offer?",
        answer:
            "I offer Marriage, Couples, Individual and Family Counselling through Healing Spaces. Sessions provide a confidential and supportive environment to work through personal, emotional and relationship concerns.",
    },
    {
        id: "location",
        label: "Location",
        question: "Where are you based?",
        answer: "I am based in Midrand, Gauteng for in-person counselling sessions.",
    },
    {
        id: "online",
        label: "Online sessions",
        question: "Do you offer online counselling?",
        answer:
            "Yes. Sessions are available both in person and virtually. Online sessions can be conducted via Zoom, Google Meet, Microsoft Teams or WhatsApp Video.",
    },
    {
        id: "duration",
        label: "Session length",
        question: "How long is a counselling session?",
        answer: "Each counselling session is 60 minutes.",
    },
    {
        id: "price",
        label: "Pricing",
        question: "How much does a counselling session cost?",
        answer:
            "The standard rate is R975 per 60-minute session when booking fewer than four sessions.",
    },
    {
        id: "packages",
        label: "Packages",
        question: "Do you offer counselling packages?",
        answer:
            "Yes. For a more structured or extended counselling journey, reduced-rate packages are available:\n\n• 4 Sessions — R3,600 (R900/session). 2 payments of R1,800 available.\n• 8 Sessions — R6,800 (R850/session). 3 payments of ≈R2,270 available.\n• 12 Sessions — R9,900 (R825/session). Payment over 2, 3 or 4 payments.",
    },
    {
        id: "which-package",
        label: "Which package?",
        question: "Which counselling package should I choose?",
        answer:
            "You're welcome to choose whichever suits your circumstances. The 4-session package is a good foundational period for exploring your concerns and beginning the process of change. The 8- and 12-session options allow for a more extended engagement where deeper or ongoing work may be needed.",
    },
    {
        id: "medical-aid",
        label: "Medical aid",
        question: "Do you accept medical aid?",
        answer:
            "At present, Healing Spaces is unable to accept medical aid. Counselling sessions are therefore privately paid.",
    },
    {
        id: "weekday-hours",
        label: "Weekday hours",
        question: "What are your weekday counselling hours?",
        answer:
            "The latest weekday start time is 17:00 for in-person sessions and 18:00 for online sessions.",
    },
    {
        id: "saturday-hours",
        label: "Saturday hours",
        question: "Are counselling sessions available on Saturdays?",
        answer:
            "Yes. Saturday sessions are available from 09:00, with the final session running from 12:00–13:00.",
    },
    {
        id: "booking",
        label: "How to book",
        question: "How do I book my first session?",
        answer:
            "When requesting an appointment, please provide three preferred dates and times. We can then identify the available option that works best for you.",
    },
    {
        id: "format-choice",
        label: "Online vs in-person",
        question: "Can I choose between online and in-person counselling?",
        answer:
            "Yes. You may select the format that is most comfortable and practical for you, subject to availability.",
    },
    {
        id: "not-just-crisis",
        label: "Not just for crisis",
        question: "Is counselling only for people whose relationships are in crisis?",
        answer:
            "No. Counselling can also provide a space for reflection, personal growth, strengthening relationships, improving communication and understanding recurring emotional or relational patterns. You do not have to wait for a crisis before seeking support.",
    },
    {
        id: "nervous",
        label: "Feeling nervous?",
        question: "I am nervous about starting counselling. Is that normal?",
        answer:
            "Absolutely. Beginning counselling can feel unfamiliar and vulnerable. Reaching out is a courageous and commendable step, and the first session provides an opportunity to begin exploring what has brought you to counselling and what you hope to work towards.",
    },
    {
        id: "contact",
        label: "Contact details",
        question: "How do I contact Jabu Zwane or Healing Spaces?",
        answer:
            "Jabu Zwane — Marriage | Couples | Individual & Family Counsellor, Healing Spaces\nWhatsApp: 061 536 3813\nBookings: bookings@jabuzwane.co.za\nEmail: jabu@jabuzwane.co.za\nWebsite: www.jabuzwane.co.za",
    },
];

// ── Component ────────────────────────────────────────────────────────────

const GREETING =
    "Hi, I'm here to help with questions about counselling with Jabu Zwane. Pick a topic below, or type your question.";

function formatInlineMarkdown(text, keyPrefix) {
    const pattern = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_|`[^`]+`)/g;
    const parts = text.split(pattern);

    return parts.map((part, index) => {
        if ((part.startsWith("**") && part.endsWith("**")) || (part.startsWith("__") && part.endsWith("__"))) {
            return <strong key={`${keyPrefix}-${index}`}>{part.slice(2, -2)}</strong>;
        }
        if ((part.startsWith("*") && part.endsWith("*")) || (part.startsWith("_") && part.endsWith("_"))) {
            return <em key={`${keyPrefix}-${index}`}>{part.slice(1, -1)}</em>;
        }
        if (part.startsWith("`") && part.endsWith("`")) {
            return <code key={`${keyPrefix}-${index}`}>{part.slice(1, -1)}</code>;
        }
        return part;
    });
}

function FormattedMessage({ text }) {
    const lines = String(text || "")
        .replace(/\u00a0|\u202f/g, " ")
        .replace(/\r\n?/g, "\n")
        .split("\n");
    const blocks = [];
    let paragraph = [];
    let list = null;

    const flushParagraph = () => {
        if (!paragraph.length) return;
        blocks.push({ type: "paragraph", lines: paragraph });
        paragraph = [];
    };
    const flushList = () => {
        if (!list) return;
        blocks.push(list);
        list = null;
    };

    lines.forEach((line) => {
        const trimmed = line.trim();
        const match = trimmed.match(/^(?:[-*•]\s+|\d+[.)]\s+)(.*)$/);
        if (!trimmed) {
            flushParagraph();
            flushList();
        } else if (match) {
            flushParagraph();
            const ordered = /^\d+[.)]\s+/.test(trimmed);
            if (!list || list.ordered !== ordered) {
                flushList();
                list = { type: "list", ordered, items: [] };
            }
            list.items.push(match[1]);
        } else {
            flushList();
            paragraph.push(trimmed);
        }
    });
    flushParagraph();
    flushList();

    return (
        <div className="fqb-rich-message">
            {blocks.map((block, blockIndex) => {
                if (block.type === "list") {
                    const List = block.ordered ? "ol" : "ul";
                    return (
                        <List key={`list-${blockIndex}`}>
                            {block.items.map((item, itemIndex) => (
                                <li key={`item-${itemIndex}`}>
                                    {formatInlineMarkdown(item, `list-${blockIndex}-${itemIndex}`)}
                                </li>
                            ))}
                        </List>
                    );
                }
                return (
                    <p key={`paragraph-${blockIndex}`}>
                        {block.lines.map((line, lineIndex) => (
                            <React.Fragment key={`line-${lineIndex}`}>
                                {lineIndex > 0 && <br />}
                                {formatInlineMarkdown(line, `paragraph-${blockIndex}-${lineIndex}`)}
                            </React.Fragment>
                        ))}
                    </p>
                );
            })}
        </div>
    );
}

const FaqChatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
    const [messages, setMessages] = useState([
        { id: "greet", from: "bot", text: GREETING },
    ]);
    const [inputValue, setInputValue] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [showAllTopics, setShowAllTopics] = useState(false);
    const scrollRef = useRef(null);
    const messagesEndRef = useRef(null); // anchor placed right after messages, before the quick-reply chips

    useEffect(() => {
        // Scroll the anchor into view rather than the container's true bottom —
        // the chips block that follows the messages would otherwise push the
        // latest answer out of view above the fold.
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [messages, isTyping, isOpen]);

    const toggleOpen = () => {
        setIsOpen((prev) => !prev);
        setHasOpenedOnce(true);
    };

    useEffect(() => {
      const openFromHomepage = () => {
        setIsOpen(true);
        setHasOpenedOnce(true);
      };
      window.addEventListener("jz:open-chat", openFromHomepage);
      return () => window.removeEventListener("jz:open-chat", openFromHomepage);
    }, []);

    const pushBotAnswer = (faq) => {
        setIsTyping(true);
        setTimeout(() => {
            setIsTyping(false);
            setMessages((prev) => [
                ...prev,
                { id: `${faq.id}-a-${Date.now()}`, from: "bot", text: faq.answer },
            ]);
        }, 650);
    };

    const handleQuickReply = (faq) => {
        setMessages((prev) => [
            ...prev,
            { id: `${faq.id}-q-${Date.now()}`, from: "user", text: faq.question },
        ]);
        pushBotAnswer(faq);
    };

    // Free-text goes to the Supabase Edge Function (Groq), which is
    // constrained to answer only from the FAQ context. Quick replies above
    // bypass this entirely and stay instant/free.
    const handleSend = (e) => {
        e.preventDefault();
        const text = inputValue.trim();
        if (!text) return;

        setMessages((prev) => [
            ...prev,
            { id: `user-${Date.now()}`, from: "user", text },
        ]);
        setInputValue("");
        setIsTyping(true);

        supabase.functions
            .invoke("faq-chat", { body: { message: text } })
            .then(({ data, error }) => {
                setIsTyping(false);
                if (error || !data?.answer) {
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: `err-${Date.now()}`,
                            from: "bot",
                            text: "Something went wrong — please try again or reach out directly.",
                        },
                    ]);
                    return;
                }
                setMessages((prev) => [
                    ...prev,
                    { id: `ai-${Date.now()}`, from: "bot", text: data.answer },
                ]);
            })
            .catch(() => {
                setIsTyping(false);
                setMessages((prev) => [
                    ...prev,
                    {
                        id: `catch-${Date.now()}`,
                        from: "bot",
                        text: "Something went wrong — please try again or reach out directly.",
                    },
                ]);
            });
    };

    return (
        <React.Fragment>
            <style>{FAQ_CHATBOT_CSS}</style>

            {isOpen && (
                <div id="fqb-chat-panel" className="fqb-panel" role="dialog" aria-modal="false" aria-label="Jabu Zwane AI assistant">
                    <div className="fqb-header">
                        <div className="fqb-header__avatar" aria-hidden="true">
                            <Bot size={22} strokeWidth={1.8} />
                            <Sparkles className="fqb-header__sparkle" size={11} fill="currentColor" />
                        </div>
                        <div className="fqb-header__info">
                            <div className="fqb-header__name">Jabu’s assistant</div>
                            <div className="fqb-header__status">
                                <span className="fqb-status-dot" />
                                AI-powered · Usually replies instantly
                            </div>
                        </div>
                        <button
                            type="button"
                            className="fqb-header__close"
                            onClick={toggleOpen}
                            aria-label="Close chat"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div className="fqb-messages" ref={scrollRef}>
                        {messages.map((m) => (
                            <div key={m.id} className={`fqb-msg fqb-msg--${m.from}`}>
                                {m.from === "bot" && <div className="fqb-msg__avatar" aria-hidden="true"><Bot size={15} /></div>}
                                <div className="fqb-msg__bubble">
                                    {m.from === "bot" ? <FormattedMessage text={m.text} /> : m.text}
                                </div>
                            </div>
                        ))}

                        {isTyping && (
                            <div className="fqb-msg fqb-msg--bot">
                                <div className="fqb-msg__avatar" aria-hidden="true"><Bot size={15} /></div>
                                <div className="fqb-msg__bubble fqb-typing">
                                    <span />
                                    <span />
                                    <span />
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />

                        <div className="fqb-quick-replies">
                            <div className="fqb-quick-replies__heading">
                                <span>Quick topics</span>
                                <button
                                    type="button"
                                    className="fqb-topics-toggle"
                                    onClick={() => setShowAllTopics((current) => !current)}
                                    aria-expanded={showAllTopics}
                                >
                                    {showAllTopics ? "Show fewer" : "More topics"}
                                    <ChevronDown size={13} className={showAllTopics ? "is-expanded" : ""} />
                                </button>
                            </div>
                            {FAQS.slice(0, showAllTopics ? FAQS.length : 4).map((faq) => (
                                <button
                                    key={faq.id}
                                    type="button"
                                    className="fqb-chip"
                                    onClick={() => handleQuickReply(faq)}
                                    disabled={isTyping}
                                >
                                    {faq.label}
                                </button>
                            ))}
                            <a href="/contact" className="fqb-chip fqb-chip--cta">
                                <MessageCircle size={14} /> Contact directly
                            </a>
                        </div>
                    </div>

                    <form className="fqb-input-row" onSubmit={handleSend}>
                        <label className="fqb-sr-only" htmlFor="fqb-message-input">Ask a question</label>
                        <input
                            id="fqb-message-input"
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder="Ask about counselling…"
                            className="fqb-input"
                            disabled={isTyping}
                        />
                        <button type="submit" className="fqb-send" disabled={isTyping || !inputValue.trim()} aria-label="Send">
                            <Send size={17} />
                        </button>
                        <span className="fqb-input-note">AI responses may not always be accurate.</span>
                    </form>
                </div>
            )}

            <button
                type="button"
                className={`fqb-fab ${isOpen ? "fqb-fab--open" : ""}`}
                onClick={toggleOpen}
                aria-label={isOpen ? "Close FAQ chat" : "Open FAQ chat"}
                aria-expanded={isOpen}
                aria-controls="fqb-chat-panel"
            >
                {!hasOpenedOnce && <span className="fqb-fab__ring" aria-hidden="true" />}
                <span className="fqb-fab__icon fqb-fab__icon--chat" aria-hidden="true">
                    <Bot size={27} strokeWidth={1.9} />
                    <Sparkles className="fqb-fab__sparkle" size={12} fill="currentColor" />
                </span>
                <span className="fqb-fab__icon fqb-fab__icon--close" aria-hidden="true"><X size={23} /></span>
            </button>
        </React.Fragment>
    );
};

// ── Styles ───────────────────────────────────────────────────────────────

const FAQ_CHATBOT_CSS = `
.fqb-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 62px;
  height: 62px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, var(--link-color), var(--link-color-strong));
  box-shadow: 0 10px 24px rgba(13, 148, 136, 0.32);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s ease;
  animation: fqb-fab-in 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.4s both;
}
.fqb-fab:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 14px 32px rgba(13, 148, 136, 0.4);
}
@keyframes fqb-fab-in {
  from { opacity: 0; transform: translateY(20px) scale(0.7); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

.fqb-fab__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid rgba(10, 132, 255, 0.55);
  animation: fqb-pulse 2s ease-out infinite;
}
@keyframes fqb-pulse {
  0%   { transform: scale(1); opacity: 0.7; }
  100% { transform: scale(1.6); opacity: 0; }
}

.fqb-fab__badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background: #ff4d4f;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  animation: fqb-badge-bounce 1.6s ease-in-out infinite;
}
@keyframes fqb-badge-bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}

.fqb-fab__icon {
  position: absolute;
  font-size: 24px;
  color: #fff;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease;
}
.fqb-fab__icon--chat { opacity: 1; transform: rotate(0deg) scale(1); }
.fqb-fab__icon--close { opacity: 0; transform: rotate(-90deg) scale(0.6); }
.fqb-fab--open .fqb-fab__icon--chat { opacity: 0; transform: rotate(90deg) scale(0.6); }
.fqb-fab--open .fqb-fab__icon--close { opacity: 1; transform: rotate(0deg) scale(1); }

.fqb-panel {
  position: fixed;
  bottom: 100px;
  right: 24px;
  width: 380px;
  max-width: calc(100vw - 32px);
  height: min(560px, 70vh);
  background: #ffffff;
  border-radius: 22px;
  box-shadow: 0 30px 70px rgba(20, 22, 26, 0.25), 0 4px 14px rgba(20, 22, 26, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 9999;
  transform-origin: bottom right;
  animation: fqb-panel-in 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
@keyframes fqb-panel-in {
  from { opacity: 0; transform: scale(0.85) translateY(16px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}

.fqb-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 18px;
    background: linear-gradient(135deg, var(--link-color), var(--link-color-strong));
  color: #fff;
  flex-shrink: 0;
}
.fqb-header__avatar {
  width: 38px; height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 13px;
  flex-shrink: 0;
}
.fqb-header__info { flex: 1; min-width: 0; }
.fqb-header__name { font-weight: 700; font-size: 14.5px; }
.fqb-header__status {
  font-size: 11.5px;
  opacity: 0.9;
  display: flex; align-items: center; gap: 6px;
  margin-top: 2px;
}
.fqb-status-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.3);
  animation: fqb-dot-pulse 1.8s ease-in-out infinite;
}
@keyframes fqb-dot-pulse {
  0%, 100% { box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.3); }
  50% { box-shadow: 0 0 0 5px rgba(74, 222, 128, 0.15); }
}
.fqb-header__close {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  width: 28px; height: 28px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 12px;
  transition: background 0.15s, transform 0.15s;
}
.fqb-header__close:hover { background: rgba(255, 255, 255, 0.28); transform: rotate(90deg); }

.fqb-messages {
  flex: 1;
  overflow-y: auto;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f8f9fb;
}

.fqb-msg {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  animation: fqb-msg-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) both;
}
@keyframes fqb-msg-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.fqb-msg--user { justify-content: flex-end; }
.fqb-msg__avatar {
  width: 24px; height: 24px; border-radius: 50%;
  background: var(--link-color); color: #fff; font-size: 9.5px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.fqb-msg__bubble {
  max-width: 78%;
  padding: 11px 14px;
  border-radius: 16px;
  font-size: 13.5px;
  line-height: 1.55;
  white-space: pre-line;
}
.fqb-msg--bot .fqb-msg__bubble {
  background: #ffffff;
  color: #14161a;
  border: 1px solid #ececee;
  border-bottom-left-radius: 4px;
}
.fqb-msg--user .fqb-msg__bubble {
  background: linear-gradient(135deg, var(--link-color), var(--link-color-strong));
  color: #fff;
  border-bottom-right-radius: 4px;
}

.fqb-typing { display: flex; align-items: center; gap: 4px; padding: 13px 16px; }
.fqb-typing span {
  width: 6px; height: 6px; border-radius: 50%;
  background: #9aa1ab;
  animation: fqb-typing-bounce 1.1s ease-in-out infinite;
}
.fqb-typing span:nth-child(2) { animation-delay: 0.15s; }
.fqb-typing span:nth-child(3) { animation-delay: 0.3s; }
@keyframes fqb-typing-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.6; }
  30% { transform: translateY(-4px); opacity: 1; }
}

.fqb-quick-replies {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}
.fqb-chip {
  font-size: 12px;
  font-weight: 600;
  padding: 8px 13px;
  border-radius: 999px;
  border: 1.5px solid var(--border-color);
  background: #fff;
  color: var(--link-color);
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s, transform 0.15s, box-shadow 0.15s;
}
.fqb-chip:hover {
  background: var(--link-color);
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 6px 14px rgba(13, 148, 136, 0.22);
}
.fqb-chip:disabled { opacity: 0.5; cursor: default; transform: none; box-shadow: none; }
.fqb-chip--cta {
  border-color: #14161a;
  color: #14161a;
}
.fqb-chip--cta:hover { background: #14161a; color: #fff; }

.fqb-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px;
  border-top: 1px solid #ececee;
  background: #fff;
  flex-shrink: 0;
}
.fqb-input {
  flex: 1;
  font-size: 13.5px;
  padding: 11px 14px;
  border-radius: 12px;
  border: 1px solid #e0e2e6;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.fqb-input:focus { border-color: var(--link-color); box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.15); }
.fqb-send {
  width: 38px; height: 38px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, var(--link-color), var(--link-color-strong));
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: transform 0.15s, opacity 0.15s;
}
.fqb-send:hover:not(:disabled) { transform: scale(1.08); }
.fqb-send:disabled { opacity: 0.4; cursor: default; }

@media (max-width: 768px) {
  .fqb-fab,
  .fqb-panel {
    display: none !important;
  }

  .fqb-panel {
    right: 16px;
    bottom: 92px;
    width: calc(100vw - 32px);
    height: min(520px, 72vh);
  }
  .fqb-fab { right: 16px; bottom: 16px; }
}

.fqb-fab {
  display: grid;
  width: 62px;
  height: 62px;
  place-items: center;
  color: #fff;
  background: linear-gradient(135deg, var(--link-color), var(--link-color-strong));
  box-shadow: 0 12px 28px rgba(13, 100, 92, .3), inset 0 1px 0 rgba(255,255,255,.24);
}
.fqb-fab:hover { transform: translateY(-3px); box-shadow: 0 16px 34px rgba(13, 100, 92, .38); }
.fqb-fab__icon { display: grid; place-items: center; }
.fqb-fab__icon--chat { font-size: inherit; }
.fqb-fab__icon--close { display: grid; }
.fqb-fab__sparkle { position: absolute; top: -5px; right: -8px; color: #d7fff1; }
.fqb-fab__ring { inset: -5px; border: 1px solid rgba(13, 148, 136, .42); }
.fqb-panel {
  right: 22px;
  bottom: 98px;
  width: min(400px, calc(100vw - 40px));
  height: min(620px, calc(100dvh - 132px));
  min-height: 390px;
  border: 1px solid rgba(23, 45, 43, .08);
  border-radius: 24px;
  box-shadow: 0 28px 80px rgba(18, 38, 36, .22), 0 5px 18px rgba(18, 38, 36, .08);
}
.fqb-header {
  gap: 12px;
  padding: 17px 18px;
  background: radial-gradient(circle at 100% 0%, rgba(255,255,255,.18), transparent 36%), linear-gradient(135deg, var(--link-color), var(--link-color-strong));
}
.fqb-header__avatar {
  position: relative;
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border: 1px solid rgba(255,255,255,.26);
  border-radius: 15px;
  background: rgba(255,255,255,.16);
}
.fqb-header__sparkle { position: absolute; top: 3px; right: 3px; color: #d8fff2; }
.fqb-header__name { font-size: 15px; letter-spacing: -.01em; }
.fqb-header__status { gap: 7px; margin-top: 4px; font-size: 11px; }
.fqb-header__close {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 11px;
  font-size: inherit;
}
.fqb-messages {
  min-height: 0;
  padding: 18px 16px 14px;
  gap: 13px;
  background: linear-gradient(180deg, #f7faf9 0%, #f3f7f6 100%);
  overscroll-behavior: contain;
  scrollbar-color: #c4d5d1 transparent;
  scrollbar-width: thin;
}
.fqb-msg { gap: 7px; }
.fqb-msg__avatar {
  display: grid;
  width: 25px;
  height: 25px;
  place-items: center;
  border: 1px solid rgba(13,148,136,.12);
  border-radius: 9px;
  background: #e4f3ef;
  color: var(--link-color-strong);
  font-size: inherit;
}
.fqb-msg__bubble {
  max-width: 82%;
  padding: 11px 13px;
  border-radius: 17px;
  font-size: 13px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}
.fqb-rich-message { min-width: 0; }
.fqb-rich-message p { margin: 0; }
.fqb-rich-message p + p,
.fqb-rich-message ul + p,
.fqb-rich-message ol + p,
.fqb-rich-message p + ul,
.fqb-rich-message p + ol,
.fqb-rich-message ul + ul,
.fqb-rich-message ul + ol,
.fqb-rich-message ol + ul,
.fqb-rich-message ol + ol { margin-top: 9px; }
.fqb-rich-message ul,
.fqb-rich-message ol {
  display: grid;
  gap: 6px;
  margin: 8px 0 0;
  padding-left: 20px;
}
.fqb-rich-message li { padding-left: 2px; }
.fqb-rich-message li::marker { color: var(--link-color); font-weight: 700; }
.fqb-rich-message strong { color: #174e48; font-weight: 750; }
.fqb-rich-message em { font-style: italic; }
.fqb-rich-message code {
  padding: 1px 5px;
  border-radius: 5px;
  background: #edf4f2;
  color: #28554f;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: .92em;
}
.fqb-msg--bot .fqb-msg__bubble {
  border: 1px solid #e4ece9;
  border-bottom-left-radius: 5px;
  background: #fff;
  color: #243431;
  box-shadow: 0 2px 6px rgba(23,45,43,.035);
}
.fqb-msg--user .fqb-msg__bubble {
  border-bottom-right-radius: 5px;
  box-shadow: 0 3px 9px rgba(13,148,136,.15);
}
.fqb-typing span { background: var(--link-color); }
.fqb-quick-replies { gap: 7px; margin: 4px 0 2px 32px; }
.fqb-quick-replies__heading {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #758783;
  font-size: 10px;
  font-weight: 750;
  letter-spacing: .08em;
  text-transform: uppercase;
}
.fqb-topics-toggle {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 0;
  border: 0;
  background: transparent;
  color: var(--link-color-strong);
  cursor: pointer;
  font: inherit;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: normal;
  text-transform: none;
}
.fqb-topics-toggle svg { transition: transform .16s ease; }
.fqb-topics-toggle svg.is-expanded { transform: rotate(180deg); }
.fqb-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 7px 11px;
  border: 1px solid #d8e6e2;
  border-radius: 999px;
  background: #fff;
  color: #276b63;
  font-size: 11px;
  font-weight: 650;
}
.fqb-chip:hover {
  border-color: var(--link-color);
  background: #eaf6f2;
  color: var(--link-color-strong);
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(13, 148, 136, .12);
}
.fqb-chip--cta { border-color: transparent; background: #e6f2ee; color: #276b63; }
.fqb-chip--cta:hover { border-color: transparent; background: #d9eee7; color: #185d55; }
.fqb-input-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 42px;
  gap: 8px;
  padding: 12px 14px 10px;
  border-top-color: #e8efed;
}
.fqb-input {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 43px;
  padding: 10px 13px;
  border-color: #dce7e4;
  border-radius: 14px;
  background: #f9fbfa;
  color: #172d2b;
  font-size: 13px;
}
.fqb-input::placeholder { color: #879692; }
.fqb-input:focus { background: #fff; }
.fqb-send {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 14px;
  font-size: inherit;
}
.fqb-send:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 5px 12px rgba(13,148,136,.24); }
.fqb-input-note {
  grid-column: 1 / -1;
  margin: -1px 2px 0;
  color: #8a9894;
  font-size: 9px;
  line-height: 1.3;
}
.fqb-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.fqb-fab:focus-visible,
.fqb-header__close:focus-visible,
.fqb-chip:focus-visible,
.fqb-topics-toggle:focus-visible,
.fqb-send:focus-visible {
  outline: 3px solid rgba(13, 148, 136, .38);
  outline-offset: 3px;
}
.fqb-fab:focus-visible { outline-color: #fff; }

@media (max-width: 768px) {
  .fqb-fab { display: grid !important; right: 16px; bottom: 16px; }
  .fqb-panel {
    display: flex !important;
    right: 12px;
    bottom: 88px;
    width: calc(100vw - 24px);
    height: min(620px, calc(100dvh - 108px));
    min-height: 360px;
    border-radius: 21px;
  }
  .fqb-messages { padding: 15px 13px 12px; }
  .fqb-quick-replies { margin-left: 29px; }
}
@media (max-width: 380px) {
  .fqb-header { padding: 14px; }
  .fqb-header__status { font-size: 10px; }
  .fqb-quick-replies { margin-left: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .fqb-fab, .fqb-fab__ring, .fqb-panel, .fqb-msg, .fqb-typing span { animation: none !important; }
  .fqb-fab, .fqb-fab__icon, .fqb-chip, .fqb-send { transition: none !important; }
}
`;

export default FaqChatbot;